import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Group, SubGroup, Relative, OptionKeyValue } from '@myriadgenetics/mgh-types';
import { useHistory, useLocation } from '../../helpers/routing';
import OptionSelect from '../question/option-select';
import Steps from './steps';

import './index.scss';
import { useTranslation } from 'react-i18next';
import { useButtonsWrapperContext } from '../buttons-wrapper';
import { BackButton } from '../buttons/back';
import Button from '../../elements/button';

interface Props {
  initGroup?: Group | null;
  initSubGroup?: SubGroup | null;
  onRelativeSelected: (relative: Relative) => void;
}

export enum RelativeStep {
  Group = 'group',
  SubGroup = 'subGroup',
  Other = 'other',
  Side = 'side',
  SideOfFamily = 'sideOfFamily',
  Selected = 'selected',
}

interface RelativeSelectorState {
  baseUrl?: any;
  group?: Group;
  subGroup?: SubGroup;
  other?: OptionKeyValue;
  step?: RelativeStep;
  sideOfFamily?: Relative;
  selected?: { [key in RelativeStep]?: OptionKeyValue };
  nextStep?: RelativeStep;
}

const stripUnselected = (subGroup: SubGroup) => subGroup.replace('.unselected', '');

const getNextStep = (subGroup: SubGroup): RelativeStep => {
  switch (subGroup) {
    case 'relatives.greatGrandmother.unselected':
    case 'relatives.greatGrandfather.unselected':
    case 'relatives.grandmother.unselected':
    case 'relatives.grandfather.unselected':
    case 'relatives.greatAunt.unselected':
    case 'relatives.greatUncle.unselected':
    case 'relatives.aunt.unselected':
    case 'relatives.uncle.unselected':
    case 'relatives.femaleCousin.unselected':
    case 'relatives.maleCousin.unselected':
    case 'relatives.halfSister.unselected':
    case 'relatives.halfBrother.unselected':
      return RelativeStep.SideOfFamily;
    default:
      return RelativeStep.Selected;
  }
};

const getStep = (initGroup: Group | null | undefined, initSubGroup: SubGroup | null | undefined): RelativeStep => {
  if (initGroup && initSubGroup) {
    return getNextStep(initSubGroup);
  } else if (initGroup && !initSubGroup) {
    return RelativeStep.SubGroup;
  } else {
    return RelativeStep.Group;
  }
};

interface LocationState {
  state: {
    rsState?: RelativeSelectorState;
  };
  pathname: string;
}

function RelativeSelector({ initGroup, initSubGroup, onRelativeSelected }: Props) {
  const { buttonsRef } = useButtonsWrapperContext();
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation() as LocationState;
  const { rsState } = location.state || {};

  useEffect(() => {
    if (!rsState) {
      history.replace(`${location.pathname}/group`, {
        ...location.state,
        title: t('pageTitles.relativeAddGroup'),
        rsState: {
          baseUrl: location.pathname,
          group: initGroup,
          subGroup: initSubGroup,
          step: getStep(initGroup, initSubGroup),
        },
      });
    }
  });

  const { baseUrl, group, subGroup, step }: RelativeSelectorState = rsState || {};

  const triggerOnRelativeSelected = (relative: Relative) => {
    onRelativeSelected(relative);
  };

  const isSubGroupStep = step === RelativeStep.SubGroup;
  const isSideOfFamilyStep = step === RelativeStep.SideOfFamily;
  const isSelf = group === 'relatives.self';

  const navigateHistoryWithState = (routeMethod: 'replace' | 'push', val: OptionKeyValue[], curStep: RelativeStep, nextStep: RelativeStep) => {
    const stepMap: { [key: string]: string } = {
      group: 'group',
      subGroup: 'subGroup',
      sideOfFamily: 'sideOfFamily',
      other: 'other',
    };

    const expectedStep = routeMethod === 'replace' ? curStep : nextStep;
    const mappedStep = stepMap[expectedStep];
    const url = `${baseUrl}/${expectedStep}`;

    const state = {
      ...location.state,
      rsState: {
        ...rsState,
        [curStep]: val[0].optionValue,
        step: mappedStep,
        selected: {
          ...(rsState?.selected ?? {}),
          [curStep]: val[0],
        },
        nextStep: nextStep,
      },
    };

    return history[routeMethod](url, state);
  };

  const joinSideOfFamily = (val: OptionKeyValue[], selectedSubGroup: SubGroup) => {
    const optionVal = val && Array.isArray(val) && val[0].optionValue;
    const sideOfFamily = optionVal === 'relatives.sideOfFamily.maternal' ? 'maternal' : 'paternal';

    return `${stripUnselected(selectedSubGroup)}.${sideOfFamily}` as Relative;
  };

  const handleOnRelativeSelected = () => {
    const isRsStateDefined = rsState !== undefined;
    const isRelativeSelected = rsState?.nextStep === RelativeStep.Selected;
    const hasStep = step !== undefined;
    const hasRelative = isRelativeSelected && hasStep && rsState?.selected?.[step]?.optionValue !== undefined;
    const selectedValue = step && rsState?.selected?.[step];

    if (!isRsStateDefined) return;

    if (hasRelative && rsState.selected?.sideOfFamily && rsState.selected?.subGroup) {
      const sideOfFamilyRelative = joinSideOfFamily([rsState.selected.sideOfFamily], rsState.selected.subGroup.optionValue as SubGroup);
      return triggerOnRelativeSelected(sideOfFamilyRelative);
    }

    if (hasRelative) {
      return triggerOnRelativeSelected(rsState.selected?.[step]?.optionValue as Relative);
    }

    if (hasStep && rsState.nextStep && rsState.selected && selectedValue) {
      return navigateHistoryWithState('push', [selectedValue], step, rsState.nextStep);
    }
  };

  return (
    <>
      <div className="relative-selector">
        {step === RelativeStep.Group && (
          <OptionSelect
            className="relative-group-selector"
            compDef={Steps.relativeGroupSelect}
            onResponseUpdated={(answerKey, val) => {
              const isSelf = val[0].optionValue === 'relatives.self';
              if (isSelf) {
                navigateHistoryWithState('replace', val, RelativeStep.Group, RelativeStep.Selected);
                triggerOnRelativeSelected('relatives.self');
                return;
              }
              navigateHistoryWithState('replace', val, RelativeStep.Group, RelativeStep.SubGroup);
              navigateHistoryWithState('push', val, RelativeStep.Group, RelativeStep.SubGroup);
              return;
            }}
            responses={{ group: rsState?.selected?.group ? [rsState?.selected?.group] : undefined }}
          />
        )}
        {group && isSubGroupStep && !isSelf && (
          <OptionSelect
            compDef={Steps.relativeGroupMap[group]}
            onResponseUpdated={(answerKey, val) => {
              const nextStep = getNextStep(val[0].optionValue);
              const selSubGroup = val[0].optionValue;
              if (nextStep === 'selected') {
                navigateHistoryWithState('replace', val, RelativeStep.SubGroup, RelativeStep.Selected);
                triggerOnRelativeSelected(selSubGroup);
                return;
              } else {
                navigateHistoryWithState('replace', val, RelativeStep.SubGroup, RelativeStep.SideOfFamily);
                navigateHistoryWithState('push', val, RelativeStep.SubGroup, RelativeStep.SideOfFamily);
              }
            }}
            responses={{ subGroup: rsState?.selected?.subGroup ? [rsState?.selected.subGroup] : undefined }}
          />
        )}
        {subGroup && isSideOfFamilyStep && (
          <OptionSelect
            compDef={Steps.sideOfFamilySelect(subGroup)}
            onResponseUpdated={(answerKey, val) => {
              const relative = joinSideOfFamily(val, subGroup);
              switch (relative) {
                case 'relatives.greatGrandmother.maternal':
                case 'relatives.greatGrandfather.maternal':
                case 'relatives.grandmother.maternal':
                case 'relatives.grandfather.maternal':
                case 'relatives.greatAunt.maternal':
                case 'relatives.greatUncle.maternal':
                case 'relatives.aunt.maternal':
                case 'relatives.uncle.maternal':
                case 'relatives.femaleCousin.maternal':
                case 'relatives.maleCousin.maternal':
                case 'relatives.halfSister.maternal':
                case 'relatives.halfBrother.maternal':
                case 'relatives.greatGrandmother.paternal':
                case 'relatives.greatGrandfather.paternal':
                case 'relatives.grandmother.paternal':
                case 'relatives.grandfather.paternal':
                case 'relatives.greatAunt.paternal':
                case 'relatives.greatUncle.paternal':
                case 'relatives.aunt.paternal':
                case 'relatives.uncle.paternal':
                case 'relatives.femaleCousin.paternal':
                case 'relatives.maleCousin.paternal':
                case 'relatives.halfSister.paternal':
                case 'relatives.halfBrother.paternal':
                  navigateHistoryWithState('replace', val, RelativeStep.SideOfFamily, RelativeStep.Selected);
                  triggerOnRelativeSelected(relative);
                  break;
                default:
              }
            }}
            responses={{ subGroup: rsState?.selected?.sideOfFamily ? [rsState?.selected?.sideOfFamily] : undefined }}
          />
        )}
      </div>
      {buttonsRef?.current &&
        createPortal(
          <>
            <BackButton />
            {rsState?.selected?.[step as RelativeStep] && (
              <Button
                disabled={!rsState?.selected?.[step as RelativeStep]}
                className="relative-selector__continue-button"
                dataTestId="continue-button"
                onClick={() => handleOnRelativeSelected()}
                size="small"
              >
                {t('continue')}
              </Button>
            )}
            <BackButton />
          </>,
          buttonsRef.current,
        )}
    </>
  );
}

export default RelativeSelector;
